import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "@/lib/utils";

export type OdsAlertType = "info" | "positive" | "negative" | "warning";

const alertBackgroundClass: Record<OdsAlertType, string> = {
  info: "bg-[var(--bg-primary-subtle)]",
  positive: "bg-[var(--bg-positive-subtle)]",
  negative: "bg-[var(--bg-negative-subtle)]",
  warning: "bg-[var(--bg-warning-subtle)]",
};

const alertTextClass: Record<OdsAlertType, string> = {
  info: "text-[var(--fg-brand)]",
  positive: "text-[var(--fg-positive)]",
  negative: "text-[var(--fg-negative)]",
  warning: "text-[var(--fg-warning)]",
};

const alertIconClass: Record<OdsAlertType, string> = {
  info: "text-[var(--fg-brand)]",
  positive: "text-[var(--border-positive)]",
  negative: "text-[var(--border-negative)]",
  warning: "text-[var(--fg-warning)]",
};

interface OdsAlertProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  type?: OdsAlertType;
}

function OdsInfoAlertIcon({ className }: { className?: string }) {
  return (
    <svg className={className} width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M10 2.5C5.86 2.5 2.5 5.86 2.5 10C2.5 14.14 5.86 17.5 10 17.5C14.14 17.5 17.5 14.14 17.5 10C17.5 5.86 14.14 2.5 10 2.5ZM10.75 13.75L9.25 13.75L9.25 9.25H10.75L10.75 13.75ZM10.75 7.75H9.25V6.25H10.75V7.75Z" fill="currentColor" />
    </svg>
  );
}

function OdsPositiveAlertIcon({ className }: { className?: string }) {
  return (
    <svg className={className} width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M9.00391 12.8125L13.6523 8.18359L12.6562 7.1875L9.00391 10.8203L7.34375 9.17969L6.34766 10.1758L9.00391 12.8125ZM10 17.5C8.97136 17.5 8.00131 17.3047 7.08984 16.9141C6.17838 16.5234 5.38086 15.9863 4.69727 15.3027C4.01367 14.6192 3.47657 13.8216 3.08594 12.9102C2.69531 11.9987 2.5 11.0286 2.5 10C2.5 8.95833 2.69531 7.98502 3.08594 7.08008C3.47657 6.17513 4.01367 5.38086 4.69727 4.69727C5.38086 4.01367 6.17838 3.47657 7.08984 3.08594C8.00131 2.69531 8.97136 2.5 10 2.5C11.0417 2.5 12.015 2.69531 12.9199 3.08594C13.8249 3.47657 14.6192 4.01367 15.3027 4.69727C15.9863 5.38086 16.5234 6.17513 16.9141 7.08008C17.3047 7.98502 17.5 8.95833 17.5 10C17.5 11.0286 17.3047 11.9987 16.9141 12.9102C16.5234 13.8216 15.9863 14.6192 15.3027 15.3027C14.6192 15.9863 13.8249 16.5234 12.9199 16.9141C12.015 17.3047 11.0417 17.5 10 17.5Z" fill="currentColor" />
    </svg>
  );
}

function OdsNegativeAlertIcon({ className }: { className?: string }) {
  return (
    <svg className={className} width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M10 2.5C5.8525 2.5 2.5 5.8525 2.5 10C2.5 14.1475 5.8525 17.5 10 17.5C14.1475 17.5 17.5 14.1475 17.5 10C17.5 5.8525 14.1475 2.5 10 2.5ZM13.75 12.6925L12.6925 13.75L10 11.0575L7.3075 13.75L6.25 12.6925L8.9425 10L6.25 7.3075L7.3075 6.25L10 8.9425L12.6925 6.25L13.75 7.3075L11.0575 10L13.75 12.6925Z" fill="currentColor" />
    </svg>
  );
}

function OdsWarningAlertIcon({ className }: { className?: string }) {
  return (
    <svg className={className} width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M3.72494 17.4999H16.2749C17.5583 17.4999 18.3583 16.1083 17.7166 14.9999L11.4416 4.15828C10.7999 3.04994 9.19994 3.04994 8.55827 4.15828L2.28327 14.9999C1.6416 16.1083 2.4416 17.4999 3.72494 17.4999ZM9.99994 11.6666C9.5416 11.6666 9.1666 11.2916 9.1666 10.8333V9.16661C9.1666 8.70828 9.5416 8.33328 9.99994 8.33328C10.4583 8.33328 10.8333 8.70828 10.8333 9.16661L10.8333 10.8333C10.8333 11.2916 10.4583 11.6666 9.99994 11.6666ZM10.8333 14.9999L9.1666 14.9999L9.1666 13.3333L10.8333 13.3333V14.9999Z" fill="currentColor" />
    </svg>
  );
}

const alertIcon = {
  info: OdsInfoAlertIcon,
  positive: OdsPositiveAlertIcon,
  negative: OdsNegativeAlertIcon,
  warning: OdsWarningAlertIcon,
};

export function OdsAlert({ children, className, type = "info", ...props }: OdsAlertProps) {
  const Icon = alertIcon[type];

  return (
    <div
      className={cn(
        "flex min-h-12 w-full items-start gap-2 rounded-lg px-3 py-3 text-[12px] font-medium leading-[120%]",
        alertBackgroundClass[type],
        className
      )}
      {...props}
    >
      <Icon className={cn("mt-px h-5 w-5 shrink-0", alertIconClass[type])} />
      <div className={cn("min-w-0 flex-1", alertTextClass[type])}>{children}</div>
    </div>
  );
}
